import { makeAutoObservable, action, observable } from 'mobx';
import { getDeliveryStatus, getJobDetail } from '@service/positionDetail';
import { getApplyInfo, getBoleInfo, recommendationNumCheck } from '@service/delivery';
import { getShareInfo } from '@service/share';
import { JOB_SPECIAL_CODE } from '@common/jobtype';
import { Toast } from 'antd-mobile';

class Delivery {
  @observable loading;

  @observable jobInfo;

  // 投递状态
  @observable statusData;

  // 根据url的token or staffId获取的伯乐信息
  @observable boleInfo;

  // 绑定的伯乐信息
  @observable bindBoleInfo;

  // 已经投递的信息
  @observable applyInfo;

  constructor() {
    makeAutoObservable(this);
  }

  @action
  getInitData = async (params, go) => {
    /* 如果是社招职位
     * 有token，staffSsoId先获取伯乐信息，伯乐码自动填充，用户可以修改，获取职位信息需要传shareType（官网 && 伯乐）
     * 获取已投递的信息 && 职位投递状态（如果社招已投递，参考职位详情显示逻辑），避免用户直接通过链接访问，导致重新投递
     *  */

    /* 如果是校招职位
     * 先获取是否绑定伯乐，如果绑定了，置灰内推码输入框
     * 如果没绑定有token，staffSsoId先获取伯乐信息，伯乐码自动填充，用户可以修改，获取职位信息需要传shareType（官网 && 伯乐）
     * 获取已投递的信息 && 职位投递状态（判断职位是否可投递，参考职位详情逻辑）
     *  */

    if (this.loading) {
      return;
    }
    this.setLoading(true);
    const { jobUnionId } = params || {};
    const data = await getJobDetail(params);
    const { projectId } = data;
    // eslint-disable-next-line prefer-const
    let [applyInfo, statusData] = await Promise.all([
      getApplyInfo({
        projectId,
        jobUnionId,
      }).catch(),
      getDeliveryStatus({
        jobId: jobUnionId,
      }).catch(),
    ]);
    const department = data?.department;
    this.statusData = statusData;
    this.jobInfo = data;
    this.setLoading(false);
    if (applyInfo?.interestBG) {
      applyInfo = {
        ...applyInfo,
        interestBG: {
          ...applyInfo?.interestBG,
          value: applyInfo?.interestBG?.code,
          label: applyInfo?.interestBG?.name,
        },
      };
    }
    this.applyInfo = applyInfo;
    const isSocial =
      data?.jobSpecialCode !== JOB_SPECIAL_CODE.YINGJIE &&
      data?.jobSpecialCode !== JOB_SPECIAL_CODE.ZHUANZHENGSHIXI &&
      data?.jobSpecialCode !== JOB_SPECIAL_CODE.BEIDOU &&
      data?.jobSpecialCode !== JOB_SPECIAL_CODE.QIHANG;

    department?.forEach((item) => {
      item.value = item.code;
      item.label = item.name;
    });
    const { staffSsoId, jobShareType, token } = params;
    const newParams = {
      jobShareType,
      staffSsoId,
      token,
    };

    if (isSocial) {
      if (token || staffSsoId) {
        // 从url上的参数获取伯乐关系 不需要判断失效关系，因为在投递页面无伯乐也是可以投递的
        const boleInfo = await getShareInfo(newParams);
        const checkBoleInfo = await recommendationNumCheck({
          recommendationNum: boleInfo?.recommendationNum,
        });
        this.boleInfo = checkBoleInfo;
        if (!boleInfo?.isEffective) {
          Toast.show({
            content: '该链接已失效，即将跳转到美团招聘官网首页',
            duration: 3000,
          });
          setTimeout(() => {
            go?.('/home', { replace: true });
          }, 2000);
          this.setLoading(false);
        }
      }
    }
    if (!isSocial && (token || staffSsoId)) {
      // 校招，url上有参数，优先取绑定信息获取的，其次再获取url上的
      // 已经绑定的伯乐关系 校招才需要获取已绑定的伯乐关系，社招每次都是新的，或者从url上重新获取
      const [bindBoleInfo, shareBoleInfo] = await Promise.all([
        getBoleInfo({ projectId, jobUnionId }),
        getShareInfo(newParams),
      ]);
      const checkBoleInfo = await recommendationNumCheck({
        recommendationNum: bindBoleInfo?.recommendationNum || shareBoleInfo?.recommendationNum,
      });
      if (bindBoleInfo?.recommendationNum) {
        this.bindBoleInfo = checkBoleInfo;
      } else {
        this.boleInfo = checkBoleInfo;
      }

      const { isApplied, isVolunteerFull, haveIntentionJob } = statusData || {};
      const isView = isVolunteerFull || !haveIntentionJob || isApplied; // 查看申请(true)按钮过来的不需要跳转，立即申请需要(false)

      // 校招立即申请进来的如果内推码失效跳转
      if (!shareBoleInfo?.isEffective && !isView) {
        Toast.show({
          content: '该链接已失效，即将跳转到美团招聘官网首页',
          duration: 3000,
        });
        setTimeout(() => {
          go?.('/home', { replace: true });
        }, 2000);
        this.setLoading(false);
      }
    }
    // 校招，无token， 无staffSsoId， 则查看是否有绑定信息
    if (!isSocial && !token && !staffSsoId) {
      const bindBoleInfo = await getBoleInfo({ projectId, jobUnionId });
      this.bindBoleInfo = bindBoleInfo;
      if (bindBoleInfo?.recommendationNum) {
        const checkBoleInfo = await recommendationNumCheck({
          recommendationNum: bindBoleInfo?.recommendationNum,
        });
        this.boleInfo = checkBoleInfo;
      }
    }

    // eslint-disable-next-line consistent-return
    return (
      !!this.boleInfo?.recommendationNum ||
      !!this.bindBoleInfo?.recommendationNum ||
      !!this.applyInfo?.recommendationNum ||
      !!this.applyInfo?.volunteerInfo?.volunteerId
    );
  };

  @action
  setLoading = (value) => {
    this.loading = value;
  };

  @action clearInfo = () => {
    this.jobInfo = null;
    this.statusData = null;
    this.applyInfo = null;
    this.boleInfo = null;
    this.bindBoleInfo = null;
    this.loading = false;
  };
}

const DeliveryStore = new Delivery();

export default DeliveryStore;
